/* Карточка «Ещё настройки» «Текста» (§1.5, BotCore.settingsCard) своей разметкой по классам прототипа (.bc-sc, .sc-h, .sc-g, .sc-s):
   общей StudioSettings не хватает ячеек-переключателей `toggle`, которые переключаются прямо на ячейке («Кто говорит», «Время реплик»).
   Нажатие шапки или ячейки choice / text — один лист «Ещё настройки» (секции: строки choice, строки-переключатели, текст), «Готово» внизу. */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { uiText } from '../../../i18n';
import { StudioIcon } from '../../studio/StudioIcon';
import { StudioSheet } from '../../studio/StudioSheet';
import { ToolIcon } from '../../studio/circle/ToolIcon';
import { SwitchRow } from '../../../ui/SwitchRow';
import { reduced } from '../../../ui/motion';

export interface TextSettingItem {
  key: string;
  title: string;
  kind: 'choice' | 'toggle' | 'text';
  icon: string;
  /** Значение ячейки словами */
  value: string;
  /** Переключатель */
  on?: boolean;
  /** Подпись строки-переключателя в листе */
  note?: string;
  wide?: boolean;
  empty?: boolean;
  /** choice: [значение, подпись, пояснение] */
  options?: Array<[string, string, string?]>;
  current?: string;
  set(value: string): void;
  /** text: своя разметка поля в листе */
  body?: ReactNode;
}
export function TextSettingsCard({ items, disabled }: { items: TextSettingItem[]; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const body = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !target) return;
    const frame = requestAnimationFrame(() => {
      const section = body.current?.querySelector<HTMLElement>(`[data-sc-sec="${target}"]`);
      section?.scrollIntoView?.({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      section?.classList.add('hl');
      window.setTimeout(() => section?.classList.remove('hl'), 700);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, target]);
  if (!items.length) return null;
  const show = (key: string | null) => {
    setTarget(key);
    setOpen(true);
  };
  const one = items.length === 1;
  return (
    <div className={`bc-sc text-sc${one ? ' one' : ''}`}>
      {!one && (
        <button type="button" className="sc-h" disabled={disabled} onClick={() => show(null)}>
          <span className="hi">
            <StudioIcon kind="sliders" />
          </span>
          <b>{uiText('Ещё настройки')}</b>
          <span className="r">
            {uiText('Изменить')}
            <StudioIcon kind="chev" />
          </span>
        </button>
      )}
      <div className="sc-g" role="group" aria-label={uiText('Ещё настройки')}>
        {items.map((item, index) => {
          const wide = item.wide || one || (items.length % 2 === 1 && index === items.length - 1);
          const toggle = item.kind === 'toggle';
          return (
            <button
              key={item.key}
              type="button"
              className={`sc-s${item.empty ? ' e' : ''}${wide ? ' w' : ''}${one ? ' row' : ''}`}
              disabled={disabled}
              role={toggle ? 'switch' : undefined}
              aria-checked={toggle ? !!item.on : undefined}
              aria-label={`${uiText(item.title)}: ${uiText(item.empty ? 'Добавить' : item.value)}`}
              onClick={() => (toggle ? item.set(item.on ? 'false' : 'true') : show(item.key))}
            >
              <span className={`sc-th${item.empty ? ' dsh' : ''}`}>
                {item.empty ? <StudioIcon kind="plus" /> : <ToolIcon kind={item.icon} />}
              </span>
              <span className="m">
                <span className="sc-k">{uiText(item.title)}</span>
                <span className="sc-v">{uiText(item.empty ? 'Добавить' : item.value)}</span>
              </span>
              {toggle && <span className={`sc-sw${item.on ? ' on' : ''}`} aria-hidden="true" />}
              {one && !toggle && (
                <span className="sc-i">
                  <StudioIcon kind="chev" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {open && (
        <StudioSheet
          title="Ещё настройки"
          onClose={() => setOpen(false)}
          footer={
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              {uiText('Готово')}
            </button>
          }
        >
          <div ref={body} className="sc-sb">
            <h2>{uiText('Ещё настройки')}</h2>
            <p className="studio-settings-sub">{uiText('Необязательно: всё уже настроено')}</p>
            <div className="studio-native-settings">
              {items.map(item => (
                <section key={item.key} className={`sc-sec sc-${item.kind}`} data-sc-sec={item.key}>
                  {item.kind !== 'toggle' && (
                    <div className="glbl">
                      {uiText(item.title)}
                      {item.kind === 'text' && <span>{uiText('необязательно')}</span>}
                    </div>
                  )}
                  {item.kind === 'toggle' ? (
                    <SwitchRow
                      title={uiText(item.title)}
                      description={uiText(item.note ?? item.value)}
                      icon={<ToolIcon kind={item.icon} />}
                      checked={!!item.on}
                      disabled={disabled}
                      onChange={value => item.set(value ? 'true' : 'false')}
                    />
                  ) : item.kind === 'choice' ? (
                    <div className="sc-list" role="radiogroup" aria-label={uiText(item.title)}>
                      {(item.options ?? []).map(([value, label, note]) => (
                        <button
                          key={value}
                          type="button"
                          className={`sc-r${item.current === value ? ' on' : ''}`}
                          role="radio"
                          aria-checked={item.current === value}
                          disabled={disabled}
                          onClick={() => item.set(value)}
                        >
                          <span className="m">
                            <b>{uiText(label)}</b>
                            {note && <small>{uiText(note)}</small>}
                          </span>
                          <span className="rd">
                            <StudioIcon kind="check" />
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    item.body
                  )}
                </section>
              ))}
            </div>
          </div>
        </StudioSheet>
      )}
    </div>
  );
}
